import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { Plus, Play, Pause, Trash2, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';

const INPUT = 'w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px] text-[#2E2E2E] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]';

function statusBadge(status) {
  const map = {
    ENABLED: 'bg-[#E3F3E8] text-[#2E844A]',
    PAUSED: 'bg-[#FFF4E5] text-[#8C4B02]',
    REMOVED: 'bg-[#FDE7E9] text-[#8E030F]',
  };
  return map[status] || 'bg-[#F3F2F2] text-[#444444]';
}

function NewCampaignModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', dailyBudget: '', finalUrls: '' });
  const [creating, setCreating] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.dailyBudget) {
      alertService.error('Missing fields', 'Name and daily budget are required');
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post('/marketing/google-ads/campaigns', {
        campaignData: {
          name: form.name.trim(),
          dailyBudget: Number(form.dailyBudget),
          finalUrls: form.finalUrls ? [form.finalUrls.trim()] : [],
        },
      });
      alertService.toast('success', `Campaign created as PAUSED — enable it when you're ready to spend.`);
      onCreated(data.campaign);
      onClose();
    } catch (err) {
      alertService.error('Failed', err.response?.data?.error || 'Could not create campaign.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md max-h-[100dvh] overflow-y-auto">
        <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <span className="text-[16px] font-bold text-[#2E2E2E]">New Search Campaign</span>
          <button onClick={onClose}><X size={18} className="text-[#5C5C5C]" /></button>
        </div>
        <div className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="text-[11px] font-semibold text-[#706E6B] block mb-1.5">Campaign Name</label>
            <input className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Spring Sale — Search" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-[#706E6B] block mb-1.5">Daily Budget</label>
            <input type="number" min="1" className={INPUT} value={form.dailyBudget} onChange={(e) => setForm({ ...form, dailyBudget: e.target.value })} placeholder="500" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-[#706E6B] block mb-1.5">Landing Page URL</label>
            <input className={INPUT} value={form.finalUrls} onChange={(e) => setForm({ ...form, finalUrls: e.target.value })} placeholder="https://example.com/landing" />
          </div>
          <div className="p-3 bg-[#EAF4FE] border border-[#B8DAF3] rounded-[4px] text-[12px] text-[#014486]">
            Every new campaign is created <strong>paused</strong>. Add ad groups, keywords, and an ad first, then enable it from the list below when you're ready to spend.
          </div>
          <Button onClick={submit} loading={creating} className="w-full">Create (Paused)</Button>
        </div>
      </div>
    </div>
  );
}

export default function GoogleAdsCampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null); // { resourceName, name, nextStatus }

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/marketing/google-ads/campaigns');
      setCampaigns(data.campaigns || []);
    } catch (err) {
      alertService.error('Failed to load campaigns', err.response?.data?.error || 'Select a Google Ads account under Settings first.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyStatusChange = async () => {
    if (!confirmTarget) return;
    try {
      await api.patch(`/marketing/google-ads/campaigns/${encodeURIComponent(confirmTarget.resourceName)}/status`, { status: confirmTarget.nextStatus });
      alertService.toast('success', `Campaign ${confirmTarget.nextStatus === 'ENABLED' ? 'enabled' : 'paused'}`);
      setConfirmTarget(null);
      load();
    } catch (err) {
      alertService.error('Failed', err.response?.data?.error || 'Could not update campaign status.');
    }
  };

  const remove = async (resourceName) => {
    if (!(await alertService.confirm('Delete?', 'Remove this campaign? Google Ads has no hard delete — this permanently ends the campaign.', 'danger', 'Delete'))) return;
    try {
      await api.delete(`/marketing/google-ads/campaigns/${encodeURIComponent(resourceName)}`);
      alertService.toast('success', 'Campaign removed');
      load();
    } catch (err) {
      alertService.error('Failed', err.response?.data?.error || 'Could not remove campaign.');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Button onClick={() => setShowModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-hidden">
        {loading ? (
          <div className="text-center py-16 text-[13px] text-[#5C5C5C]">Loading…</div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-16 text-[13px] text-[#5C5C5C]">No campaigns yet — create your first Search campaign above.</div>
        ) : (
          <div className="slds2-table-wrap"><table className="w-full text-[13px]">
            <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Daily Budget</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.resourceName} className="border-t border-[#F3F2F2]">
                  <td className="px-4 py-2.5 font-medium text-[#2E2E2E]">{c.name}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${statusBadge(c.status)}`}>{c.status}</span>
                  </td>
                  <td className="px-4 py-2.5 text-[#5C5C5C]">{c.dailyBudgetMicros ? `${(c.dailyBudgetMicros / 1e6).toFixed(2)}` : '—'}</td>
                  <td className="px-4 py-2.5 text-right space-x-2">
                    {c.status === 'PAUSED' && (
                      <button onClick={() => setConfirmTarget({ resourceName: c.resourceName, name: c.name, nextStatus: 'ENABLED' })} className="text-[#2E844A] hover:text-[#036B3A]" title="Enable">
                        <Play size={16} />
                      </button>
                    )}
                    {c.status === 'ENABLED' && (
                      <button onClick={() => setConfirmTarget({ resourceName: c.resourceName, name: c.name, nextStatus: 'PAUSED' })} className="text-[#DD7A01] hover:text-[#8C4B02]" title="Pause">
                        <Pause size={16} />
                      </button>
                    )}
                    {c.status !== 'REMOVED' && (
                      <button onClick={() => remove(c.resourceName)} className="text-[#BA0517] hover:text-[#8E030F]" title="Remove">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {showModal && <NewCampaignModal onClose={() => setShowModal(false)} onCreated={() => load()} />}

      {confirmTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-sm max-h-[100dvh] overflow-y-auto p-4 sm:p-6 space-y-4">
            <p className="text-[16px] font-bold text-[#2E2E2E]">
              {confirmTarget.nextStatus === 'ENABLED' ? 'Enable this campaign?' : 'Pause this campaign?'}
            </p>
            <p className="text-[13px] text-[#5C5C5C]">
              <strong>{confirmTarget.name}</strong>
              {confirmTarget.nextStatus === 'ENABLED'
                ? ' will start spending against its daily budget immediately on Google Ads.'
                : ' will stop serving ads and spending immediately.'}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmTarget(null)}>Cancel</Button>
              <Button onClick={applyStatusChange}>{confirmTarget.nextStatus === 'ENABLED' ? 'Enable & Start Spending' : 'Pause'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
